import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type {
  ICourseDetail,
  ICourseMembershipOption,
  ICourseRenewOptionPricing,
} from '../api/client';
import { useToast } from '../context/ToastContext';
import { Button, FormField } from './ui';
import Modal from './Modal';
import PricingTiersField, {
  PriceTier,
  PricingTiersValue,
  generateDefaultPrices,
  STANDARD_DURATIONS,
} from '../schemaForm/fields/PricingTiersField';
import { seasonLabel } from '../utils/seasonLabel';
import { formatPrice } from '../utils/coursePricing';
import '../App.css';
import { translateError } from '../utils/errorMessages';

interface CourseRenewalModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The (expiring) source course being renewed. */
  course: ICourseDetail;
  /** Navigate here on success — passed the new course id. */
  onRenewed: (newCourseId: number) => void;
}

/** A template as returned by GET /api/courses/:id/schedule. */
interface ScheduleTemplate {
  _id: number;
  name: string;
  startTime: string;
  endTime: string;
  recurringPattern: string;
  firstOccurrence: string;
  isActive: boolean;
}

type WizardStep = 1 | 2 | 3 | 4;

/** Add whole years to an ISO date string ('YYYY-MM-DD'), keeping month/day. */
const addYears = (iso: string, years: number): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const year = Number(m[1]) + years;
  return `${String(year).padStart(4, '0')}-${m[2]}-${m[3]}`;
};

/** A calendar date in the org timezone. `month` is 1-12 (NOT JS 0-based). */
interface CalDate {
  year: number;
  month: number;
  day: number;
}

/** Normalize via Date.UTC so day/month overflow (e.g. Jan 32 → Feb 1) resolves. */
const normalizeCalDate = (year: number, month: number, day: number): CalDate => {
  const d = new Date(Date.UTC(year, month - 1, day));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
};

/** Compare two calendar dates: <0 if a before b, 0 equal, >0 after. */
const compareCalDate = (a: CalDate, b: CalDate): number => {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
};

/** Next occurrence from `current`, anchored on `anchor` (mirrors eventGenerator). */
const nextOccurrence = (
  current: CalDate,
  anchor: CalDate,
  pattern: string,
): CalDate => {
  switch (pattern) {
    case 'daily':
      return normalizeCalDate(current.year, current.month, current.day + 1);
    case 'biweekly':
      return normalizeCalDate(current.year, current.month, current.day + 14);
    case 'monthly': {
      const nextMonth = current.month + 1;
      const year = current.year + (nextMonth > 12 ? 1 : 0);
      const month = nextMonth > 12 ? 1 : nextMonth;
      // Clamp e.g. the 31st into shorter months (last day of the target month).
      const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
      return { year, month, day: Math.min(anchor.day, daysInMonth) };
    }
    case 'weekly':
    default:
      return normalizeCalDate(current.year, current.month, current.day + 7);
  }
};

/**
 * Preview the first session of the renewed season for a template, mirroring the
 * backend event generator (`eventGenerator.ts`). Carry-forward keeps the
 * template's ORIGINAL `firstOccurrence` (it does NOT shift it), and generation
 * anchors recurrence on that Prague CALENDAR DATE, walking forward by the pattern
 * to the first occurrence ON OR AFTER the new term start. So the preview is that
 * same first occurrence — same weekday as the original for weekly/biweekly, same
 * day-of-month for monthly. Returns a LOCAL-noon anchor so a ±1h DST drift can
 * never flip the displayed day.
 */
const firstSessionOfNewTerm = (
  firstOccurrence: string,
  recurringPattern: string,
  newTermStart: string,
): Date => {
  // Original firstOccurrence's Prague calendar date — the recurrence anchor.
  const origPragueDate = new Date(firstOccurrence).toLocaleDateString('en-CA', {
    timeZone: 'Europe/Prague',
  });
  const [oy, om, od] = origPragueDate.split('-').map(Number);
  const anchor: CalDate = { year: oy, month: om, day: od };

  const [ty, tm, td] = newTermStart.split('-').map(Number);
  const termStartCal: CalDate = { year: ty, month: tm, day: td };

  // Walk occurrences forward until the first one lands on or after the new term
  // start (weekday/day-of-month preserved). Guard against a corrupt, non-
  // advancing pattern — every real pattern strictly advances the date.
  let current = anchor;
  let guard = 0;
  while (compareCalDate(current, termStartCal) < 0 && guard < 1000) {
    current = nextOccurrence(current, anchor, recurringPattern);
    guard += 1;
  }

  // Local-noon anchor: a one-hour DST drift can never flip a noon anchor's day.
  return new Date(current.year, current.month - 1, current.day, 12, 0, 0);
};

/**
 * Seed a full pricing-tier editor value for a TIME option from its current
 * `prices` ladder (labels / durations / default preserved). Falls back to the
 * derived `priceOptions` / flat `price` when the raw ladder is unavailable, so
 * the editor is never empty. `yearlyPrice` is seeded from the default (or
 * highest) tier so PricingTiersField's auto-regeneration behaves sensibly.
 */
const seedTimePricing = (opt: ICourseMembershipOption): PricingTiersValue => {
  const rawTiers = Array.isArray(opt.prices) ? opt.prices : [];
  if (rawTiers.length > 0) {
    const prices: PriceTier[] = rawTiers.map((tier) => {
      const price =
        typeof tier.price === 'number' ? tier.price : parseFloat(String(tier.price)) || 0;
      return {
        durationDays: tier.durationDays,
        price,
        label: tier.label ?? '',
        sortOrder: tier.sortOrder ?? 0,
        isDefault: !!tier.isDefault,
        isCustom: !STANDARD_DURATIONS.includes(tier.durationDays),
      };
    });
    const def = prices.find((p) => p.isDefault) ?? prices[prices.length - 1];
    return { yearlyPrice: def ? def.price : 0, prices, manuallyEdited: true };
  }
  // Fallback: seed from the derived price options / flat price.
  const amounts = (opt.priceOptions ?? [])
    .map((p) => p.amount)
    .filter((n) => typeof n === 'number' && Number.isFinite(n));
  const fallback = opt.price != null ? parseFloat(String(opt.price)) : NaN;
  const yearly = amounts.length > 0 ? Math.max(...amounts) : Number.isFinite(fallback) ? fallback : 0;
  return { yearlyPrice: yearly, prices: generateDefaultPrices(yearly), manuallyEdited: false };
};

/**
 * Multi-step wizard to prepare the next season of a course (the current season
 * stays active until its term ends): (1) confirm the new term (defaults to old
 * term + 1 year), (2) preview the recurring schedule — an informational preview
 * of the current slots and when each would first run in the new term (weekday
 * preserved). Under Model 3 (deferred carry) the schedule stays with the active
 * season and carries into the new season automatically when the current term
 * ends; nothing is carried at prepare-time. (3) review/edit each option's full
 * pricing — a tier editor for TIME options, a read-only carry-forward summary
 * for ENTRY/CALENDAR options, (4) toggle roster transfer and confirm. Reuses the
 * shared Modal + Button + FormField primitives and the PricingTiersField widget;
 * introduces no new modal/form system.
 */
const CourseRenewalModal: React.FC<CourseRenewalModalProps> = ({
  isOpen,
  onClose,
  course,
  onRenewed,
}) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();

  const [step, setStep] = useState<WizardStep>(1);
  const [termStart, setTermStart] = useState('');
  const [termEnd, setTermEnd] = useState('');
  const [transferRoster, setTransferRoster] = useState(false);
  // Full per-option pricing editors keyed by SOURCE membership id. Only TIME
  // options are editable; entry/calendar carry their pricing forward unchanged.
  const [optionPricing, setOptionPricing] = useState<Record<number, PricingTiersValue>>({});
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const options = useMemo<ICourseMembershipOption[]>(
    () => (course.memberships ?? []) as ICourseMembershipOption[],
    [course.memberships],
  );
  const timeOptions = useMemo(() => options.filter((o) => o.type === 'time'), [options]);
  const carriedOptions = useMemo(() => options.filter((o) => o.type !== 'time'), [options]);

  // Initialize the form each time the modal opens: default new term = old + 1yr,
  // per-time-option tier editors seeded from each option's full price ladder.
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setTransferRoster(false);
    setTermStart(course.termStart ? addYears(course.termStart.slice(0, 10), 1) : '');
    setTermEnd(course.termEnd ? addYears(course.termEnd.slice(0, 10), 1) : '');
    const seeded: Record<number, PricingTiersValue> = {};
    for (const opt of timeOptions) {
      seeded[opt._id] = seedTimePricing(opt);
    }
    setOptionPricing(seeded);
  }, [isOpen, course.termStart, course.termEnd, timeOptions]);

  // Load the source course's templates for the preview step.
  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const { data } = await api.courses.getSchedule(Number(course._id));
      setTemplates((data as { templates: ScheduleTemplate[] }).templates ?? []);
    } catch {
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, [course._id]);

  useEffect(() => {
    if (isOpen) loadTemplates();
  }, [isOpen, loadTemplates]);

  const oldSeason = useMemo(
    () => seasonLabel(course.termStart, course.termEnd, t, i18n.language),
    [course.termStart, course.termEnd, t, i18n.language],
  );
  const newSeason = useMemo(
    () => seasonLabel(termStart || null, termEnd || null, t, i18n.language),
    [termStart, termEnd, t, i18n.language],
  );

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  const termValid = termStart !== '' && termEnd !== '' && termEnd >= termStart;

  const handleConfirm = async () => {
    if (!termValid) return;
    setSubmitting(true);
    try {
      // Build the per-option payload from the TIME-option tier editors only.
      // Entry/calendar options are omitted so the backend carries their pricing
      // forward unchanged. Strip the UI-only `isCustom` flag from tiers.
      const optionPricingPayload: ICourseRenewOptionPricing[] = timeOptions.map((opt) => {
        const value = optionPricing[opt._id] ?? seedTimePricing(opt);
        return {
          sourceMembershipId: opt._id,
          type: 'time',
          yearlyPrice: value.yearlyPrice,
          prices: value.prices.map(({ isCustom, ...tier }) => tier),
        };
      });
      const { data } = await api.courses.renew(Number(course._id), {
        termStart,
        termEnd,
        transferRoster,
        optionPricing: optionPricingPayload,
      });
      addToast(t('courses.renew.success'), 'success');
      onRenewed(data.courseId);
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('courses.renew.title')} size="large">
      <p className="form-help-text" style={{ marginTop: 0 }}>
        {t('courses.renew.intro', { from: oldSeason, to: newSeason })}
      </p>

      {/* Step 1 — new term */}
      {step === 1 && (
        <div className="form-section">
          <h4 className="form-section__title">{t('courses.renew.step1Title')}</h4>
          <FormField label={t('courses.termStart')} htmlFor="renew-term-start">
            <input
              id="renew-term-start"
              type="date"
              value={termStart}
              onChange={(e) => setTermStart(e.target.value)}
            />
          </FormField>
          <FormField
            label={t('courses.termEnd')}
            htmlFor="renew-term-end"
            error={
              termStart !== '' && termEnd !== '' && termEnd < termStart
                ? t('courses.renew.termOrderError')
                : undefined
            }
          >
            <input
              id="renew-term-end"
              type="date"
              value={termEnd}
              onChange={(e) => setTermEnd(e.target.value)}
            />
          </FormField>
        </div>
      )}

      {/* Step 2 — recurring schedule preview (deferred carry) */}
      {step === 2 && (
        <div className="form-section">
          <h4 className="form-section__title">{t('courses.renew.step2Title')}</h4>
          <p className="form-help-text" style={{ marginTop: 0 }}>
            {t('courses.renew.step2Help')}
          </p>
          {templatesLoading ? (
            <p>{t('common.loading')}</p>
          ) : templates.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>{t('courses.renew.noTemplates')}</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {templates.map((tpl) => (
                <li
                  key={tpl._id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid var(--bg-light)',
                  }}
                >
                  <span>{tpl.name}</span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {t('courses.renew.firstOn', {
                      date: dateFmt.format(
                        firstSessionOfNewTerm(tpl.firstOccurrence, tpl.recurringPattern, termStart),
                      ),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Step 3 — per-option pricing: full tier editor (time) + carry-forward
          summary (entry/calendar) */}
      {step === 3 && (
        <div className="form-section">
          <h4 className="form-section__title">{t('courses.renew.step3Title')}</h4>
          {options.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>{t('courses.renew.noOptions')}</p>
          ) : (
            <>
              {timeOptions.map((opt) => (
                <div
                  key={opt._id}
                  style={{
                    padding: '0.75rem 0',
                    borderBottom: '1px solid var(--bg-light)',
                  }}
                >
                  <h5 style={{ margin: '0 0 0.5rem' }}>{opt.name}</h5>
                  <PricingTiersField
                    field={{
                      name: `renew-opt-${opt._id}`,
                      kind: 'json',
                      required: false,
                      labelKey: '',
                    }}
                    value={optionPricing[opt._id]}
                    onChange={(v: PricingTiersValue) =>
                      setOptionPricing((prev) => ({ ...prev, [opt._id]: v }))
                    }
                  />
                </div>
              ))}

              {carriedOptions.length > 0 && (
                <div style={{ marginTop: timeOptions.length > 0 ? '1rem' : 0 }}>
                  <p className="form-help-text" style={{ marginTop: 0 }}>
                    {t('courses.renew.carriedForwardHelp')}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {carriedOptions.map((opt) => {
                      const prices = (opt.priceOptions ?? [])
                        .map((p) => formatPrice(p.amount, i18n.language))
                        .filter((s): s is string => s != null);
                      const summary =
                        prices.length > 0
                          ? prices.join(' · ')
                          : formatPrice(opt.price, i18n.language) ?? t('courses.renew.noPrice');
                      return (
                        <li
                          key={opt._id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '1rem',
                            padding: '0.5rem 0',
                            borderBottom: '1px solid var(--bg-light)',
                          }}
                        >
                          <span>{opt.name}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{summary}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Step 4 — roster transfer + confirm */}
      {step === 4 && (
        <div className="form-section">
          <h4 className="form-section__title">{t('courses.renew.step4Title')}</h4>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={transferRoster}
              onChange={(e) => setTransferRoster(e.target.checked)}
            />
            <span>{t('courses.renew.transferRoster')}</span>
          </label>
          <p className="form-help-text">{t('courses.renew.transferRosterHelp')}</p>
          <p style={{ marginTop: '1rem' }}>
            {t('courses.renew.confirmSummary', { season: newSeason })}
          </p>
        </div>
      )}

      {/* Step navigation */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '1.5rem',
          gap: '0.75rem',
        }}
      >
        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {t('courses.renew.stepIndicator', { current: step, total: 4 })}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {step > 1 && (
            <Button
              variant="secondary"
              onClick={() => setStep((s) => (s - 1) as WizardStep)}
              disabled={submitting}
            >
              {t('common.back')}
            </Button>
          )}
          {step < 4 ? (
            <Button
              variant="primary"
              onClick={() => setStep((s) => (s + 1) as WizardStep)}
              disabled={step === 1 && !termValid}
            >
              {t('common.next')}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={submitting}
              disabled={!termValid}
            >
              {t('courses.renew.confirm')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CourseRenewalModal;
